import type {
  PageMetrics,
  ViewportPadding,
  ViewportPoint,
  ViewportRect,
  ViewportSize,
} from "@/features/document-viewport/types";

export const MAX_VIEWPORT_ZOOM = 10;

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function getDefaultViewportPadding(
  partial?: Partial<ViewportPadding>
): ViewportPadding {
  return {
    top: partial?.top ?? 60,
    right: partial?.right ?? 60,
    bottom: partial?.bottom ?? 60,
    left: partial?.left ?? 60,
  };
}

export function getViewportContentRect(
  workspaceSize: ViewportSize,
  viewportPadding: ViewportPadding
) {
  const width = Math.max(
    1,
    workspaceSize.width - viewportPadding.left - viewportPadding.right
  );
  const height = Math.max(
    1,
    workspaceSize.height - viewportPadding.top - viewportPadding.bottom
  );

  return {
    x: viewportPadding.left,
    y: viewportPadding.top,
    width,
    height,
  };
}

export function getViewportContentCenter(
  workspaceSize: ViewportSize,
  viewportPadding: ViewportPadding
) {
  const contentRect = getViewportContentRect(workspaceSize, viewportPadding);
  return {
    x: contentRect.x + contentRect.width / 2,
    y: contentRect.y + contentRect.height / 2,
  };
}

export function getFitPageZoom(
  workspaceSize: ViewportSize,
  viewportPadding: ViewportPadding,
  pageMetrics: PageMetrics | null
) {
  if (!pageMetrics || pageMetrics.width <= 0 || pageMetrics.height <= 0) {
    return 1;
  }

  const contentRect = getViewportContentRect(workspaceSize, viewportPadding);
  const scaleX = contentRect.width / pageMetrics.width;
  const scaleY = contentRect.height / pageMetrics.height;
  return clamp(Math.min(scaleX, scaleY), 0.35, MAX_VIEWPORT_ZOOM);
}

export function getDocumentFrame(args: {
  workspaceSize: ViewportSize;
  viewportPadding: ViewportPadding;
  pageMetrics: PageMetrics | null;
  zoom: number;
  pan: ViewportPoint;
}) {
  const { workspaceSize, viewportPadding, pageMetrics, zoom, pan } = args;
  const contentRect = getViewportContentRect(workspaceSize, viewportPadding);
  const baseWidth = contentRect.width;
  const aspectRatio = pageMetrics?.aspectRatio ?? 1.414;
  const baseHeight = baseWidth * aspectRatio;
  const screenWidth = baseWidth * zoom;
  const screenHeight = baseHeight * zoom;
  const centerX = contentRect.x + contentRect.width / 2;
  const centerY = contentRect.y + contentRect.height / 2;

  return {
    contentRect,
    centerX,
    centerY,
    baseWidth,
    baseHeight,
    screenWidth,
    screenHeight,
    originX: centerX - screenWidth / 2 + pan.x,
    originY: centerY - screenHeight / 2 + pan.y,
  };
}

export function clampPanToDocumentBounds(args: {
  workspaceSize: ViewportSize;
  viewportPadding: ViewportPadding;
  pageMetrics: PageMetrics | null;
  zoom: number;
  pan: ViewportPoint;
  overscroll?: number;
}): ViewportPoint {
  const {
    workspaceSize,
    viewportPadding,
    pageMetrics,
    zoom,
    pan,
    overscroll = 96,
  } = args;
  const frame = getDocumentFrame({
    workspaceSize,
    viewportPadding,
    pageMetrics,
    zoom,
    pan,
  });

  const { contentRect, screenWidth, screenHeight } = frame;
  const centeredPan = { x: 0, y: 0 };

  if (screenWidth <= contentRect.width) {
    centeredPan.x = 0;
  } else {
    const minPanX =
      contentRect.width / 2 - screenWidth / 2 - overscroll;
    const maxPanX =
      screenWidth / 2 - contentRect.width / 2 + overscroll;
    centeredPan.x = clamp(pan.x, minPanX, maxPanX);
  }

  if (screenHeight <= contentRect.height) {
    centeredPan.y = 0;
  } else {
    const minPanY =
      contentRect.height / 2 - screenHeight / 2 - overscroll;
    const maxPanY =
      screenHeight / 2 - contentRect.height / 2 + overscroll;
    centeredPan.y = clamp(pan.y, minPanY, maxPanY);
  }

  return centeredPan;
}

export function screenToPagePoint(args: {
  point: ViewportPoint;
  frame: ReturnType<typeof getDocumentFrame>;
}) {
  const { point, frame } = args;
  if (frame.screenWidth <= 0 || frame.screenHeight <= 0) {
    return null;
  }

  const x = (point.x - frame.originX) / frame.screenWidth;
  const y = (point.y - frame.originY) / frame.screenHeight;

  return {
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    inside: x >= 0 && x <= 1 && y >= 0 && y <= 1,
  };
}

export function pageToScreenPoint(args: {
  point: ViewportPoint;
  frame: ReturnType<typeof getDocumentFrame>;
}): ViewportPoint {
  const { point, frame } = args;
  return {
    x: frame.originX + point.x * frame.screenWidth,
    y: frame.originY + point.y * frame.screenHeight,
  };
}

export function pageRectToScreenRect(args: {
  rect: ViewportRect;
  frame: ReturnType<typeof getDocumentFrame>;
}): ViewportRect {
  const { rect, frame } = args;
  const origin = pageToScreenPoint({
    point: { x: rect.x, y: rect.y },
    frame,
  });

  return {
    x: origin.x,
    y: origin.y,
    width: rect.width * frame.screenWidth,
    height: rect.height * frame.screenHeight,
  };
}

export function screenRectToPageRect(args: {
  rect: ViewportRect;
  frame: ReturnType<typeof getDocumentFrame>;
}) {
  const { rect, frame } = args;
  const topLeft = screenToPagePoint({
    point: { x: rect.x, y: rect.y },
    frame,
  });
  const bottomRight = screenToPagePoint({
    point: { x: rect.x + rect.width, y: rect.y + rect.height },
    frame,
  });

  if (!topLeft || !bottomRight) {
    return null;
  }

  return {
    x: topLeft.x,
    y: topLeft.y,
    width: Math.max(0, bottomRight.x - topLeft.x),
    height: Math.max(0, bottomRight.y - topLeft.y),
  };
}

export function getRenderScaleForZoom(zoom: number) {
  return clamp(Math.max(1, Math.round(zoom * 2) / 2), 1, 6);
}
